import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, ClipboardList, Luggage, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, fmtDate, inr } from "@/lib/api";
import { useAdminSettings } from "@/lib/useAdminSettings";
import { Empty, PageHeader, Panel } from "@/components/admin/common";
import { BriefingDialog } from "@/components/admin/BriefingDialog";

const WINDOWS = ["3", "7", "14", "30"];

const When = ({ b }) => {
  const d = b.days_to_travel;
  if (b.briefing_sent_at) return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`briefing-status-${b.id}`}><CheckCircle2 className="h-3.5 w-3.5" />Sent {fmtDate(b.briefing_sent_at)}</span>;
  const [text, cls] = d <= 2 ? [d === 2 ? "Send today" : d === 1 ? "Overdue · travels tomorrow" : "Overdue · travels today", "bg-coral text-white"]
    : [`Send in ${d - 2} day${d - 2 === 1 ? "" : "s"}`, "bg-sky-50 text-sky-800"];
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`} data-testid={`briefing-status-${b.id}`}>{text}</span>;
};

const Row = ({ b, onOpen }) => {
  const ready = b.briefing_pickup && b.briefing_ferry;
  return (
    <Panel className="p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`briefing-row-${b.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap"><p className="font-semibold text-navy">{b.customer_name}</p><When b={b} /></div>
        <p className="text-xs text-slate-500 truncate">{b.booking_no} · {b.phone} · {b.package_name || "Custom package"} · {b.guests} guests</p>
        <p className="text-xs text-slate-500 mt-1">Travel {fmtDate(b.travel_date)}{b.balance_due > 0 && <span className="text-coral font-semibold"> · Balance due {inr(b.balance_due)}</span>}</p>
      </div>
      <div className="flex items-center gap-3">
        {ready ? <span className="text-xs text-emerald-700 flex items-center gap-1" data-testid={`briefing-ready-${b.id}`}><CheckCircle2 className="h-4 w-4" />Pickup & ferry added</span>
          : <span className="text-xs text-amber-700 flex items-center gap-1" data-testid={`briefing-missing-${b.id}`}><TriangleAlert className="h-4 w-4" />Add pickup & ferry details</span>}
        <Button className="bg-ocean hover:bg-navy" onClick={() => onOpen(b)} data-testid={`briefing-open-${b.id}`}><ClipboardList className="h-4 w-4 mr-2" />{b.briefing_sent_at ? "View / resend" : "Prepare briefing"}</Button>
      </div>
    </Panel>
  );
};

export default function Briefings() {
  const settings = useAdminSettings();
  const [days, setDays] = useState("7");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/briefings", { params: { days } }).then((r) => setData(r.data)), [days]);
  useEffect(() => { load(); }, [load]);

  const changed = (nb) => {
    setData((d) => ({ ...d, bookings: d.bookings.map((x) => (x.id === nb.id ? { ...x, ...nb } : x)) }));
    setOpen((o) => (o && o.id === nb.id ? { ...o, ...nb } : o));
  };
  const dueToday = data?.bookings.filter((b) => !b.briefing_sent_at && b.days_to_travel <= 2).length || 0;

  return (
    <div data-testid="admin-briefings-page">
      <PageHeader title="Trip briefings" subtitle="Send each guest a WhatsApp checklist two days before travel: pickup, ferry times, what to pack and balance due." />
      <div className="grid gap-4 sm:grid-cols-3 mb-4">
        <Panel className="p-5"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Upcoming trips</p><p className="mt-2 font-mono text-2xl font-semibold text-navy" data-testid="briefings-count">{data?.count ?? "-"}</p></Panel>
        <Panel className="p-5"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Send today</p><p className="mt-2 font-mono text-2xl font-semibold text-coral" data-testid="briefings-due-today">{data ? dueToday : "-"}</p></Panel>
        <Panel className="p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Travelling within</p>
          <div className="mt-2 flex gap-1.5 flex-wrap">
            {WINDOWS.map((w) => (
              <button key={w} onClick={() => setDays(w)} data-testid={`briefings-window-${w}`}
                className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${days === w ? "bg-ocean text-white border-ocean" : "border-slate-200 text-slate-600 hover:border-ocean"}`}>{w} days</button>
            ))}
          </div>
        </Panel>
      </div>
      {!data ? <Skeleton className="h-40 rounded-2xl" /> : data.bookings.length === 0 ? (
        <Panel><Empty testId="briefings-empty"><Luggage className="h-8 w-8 mx-auto mb-3 text-ocean" />No confirmed trips in the next {days} days.</Empty></Panel>
      ) : <div className="space-y-3">{data.bookings.map((b) => <Row key={b.id} b={b} onOpen={setOpen} />)}</div>}
      <BriefingDialog booking={open} settings={settings} onClose={() => setOpen(null)} onChanged={changed} />
    </div>
  );
}
